import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { config } from '../../config/environment';
import api, { API_ENDPOINTS } from '../../services/api';

const CATEGORIES = [
  { value: 'COMPLAINT', label: 'Complaint' },
  { value: 'SUGGESTIONS', label: 'Suggestions' },
  { value: 'ADVERTISEMENTS', label: 'Advertisements' },
  { value: 'INFORMATION_REQUIRED', label: 'Information required' },
  { value: 'GENERAL', label: 'General' },
] as const;

const MAX_MESSAGE_LENGTH = 2000;

const ContactPage: React.FC = () => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const recaptchaSiteKey = config.services.recaptchaSiteKey;

  const [form, setForm] = useState({
    category: 'GENERAL',
    message: '',
    name: '',
    email: '',
  });
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (field === 'message' && value.length > MAX_MESSAGE_LENGTH) return;
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token || '');
    setError(null);
  };

  const tokenToSend = recaptchaSiteKey ? recaptchaToken : 'dev-bypass';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (recaptchaSiteKey && !recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      return;
    }
    if (!form.message.trim()) {
      setError('Please enter your message.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const baseUrl = config.coreApiUrl;
      const res = await api.post(`${baseUrl}${API_ENDPOINTS.CONTACT.SUBMIT}`, {
        category: form.category,
        message: form.message.trim(),
        name: form.name.trim() || undefined,
        email: form.email.trim() || undefined,
        recaptchaToken: tokenToSend,
      });
      if (res.data?.success) {
        setSuccess(true);
        setForm({ category: 'GENERAL', message: '', name: '', email: '' });
        setRecaptchaToken('');
        recaptchaRef.current?.reset();
      } else {
        setError(res.data?.message || 'Submission failed.');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (err as Error)?.message
        || 'Unable to submit. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
          Contact Us
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Send us your feedback, complaints, suggestions, or enquiries. We’ll get back to you as soon as we can.
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Thank you. Your message has been received.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            select
            fullWidth
            label="Category"
            value={form.category}
            onChange={handleChange('category')}
            sx={{ mb: 2 }}
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Message"
            value={form.message}
            onChange={handleChange('message')}
            placeholder="Type your message (max 2000 characters)"
            inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
            helperText={`${form.message.length} / ${MAX_MESSAGE_LENGTH}`}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Your name (optional)"
            value={form.name}
            onChange={handleChange('name')}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="email"
            label="Your email (optional)"
            value={form.email}
            onChange={handleChange('email')}
            sx={{ mb: 2 }}
          />

          {recaptchaSiteKey ? (
            <Box sx={{ mb: 3 }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={recaptchaSiteKey}
                onChange={handleRecaptchaChange}
                onExpired={() => setRecaptchaToken('')}
              />
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              CAPTCHA not configured (set VITE_RECAPTCHA_SITE_KEY). Submissions only work when backend secret is also unset (dev).
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || !form.message.trim()}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Sending…' : 'Send message'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ContactPage;
